/**
 * Патч прототипа Kendo UI Validator: подмена `validate` (окно с ошибками), `validateInput` (подсветка по blur)
 * и мягкий патч `reset`; установка идемпотентна и не бросает исключений.
 */

import { applyFieldErrorHighlight, clearFieldErrorHighlights, resolveFieldInput } from './highlight';
import { showValidationErrorsSafely } from './presenter';

type ValidatorInstance = kendo.ui.BackOfficeValidatorInstance;

const VALIDATION_POPUP_DATA_KEY = 'validation-popup';
const VALIDATION_POPUP_SCOPE_SELECTOR = '[data-validation-popup-scope="off"]';

let isOverrideInstalled = false;
let isValidationInProgress = false;

/**
 * Прототип валидатора, на который вешается оверрайд. Нужны оба жёстких метода (`validate`, `validateInput`):
 * иначе прототип недоступен и попытка повторяется по `$(document).ready` — частично установленный оверрайд
 * не считается установленным. Мягкий патч `reset` в это требование не входит.
 */
function resolveValidatorPrototype(): kendo.ui.BackOfficeValidatorPrototype | undefined {
    const validatorPrototype = kendo.ui.Validator.fn;

    if (typeof validatorPrototype.validate === 'function'
        && typeof validatorPrototype.validateInput === 'function') {
        return validatorPrototype;
    }
}

function isPatchedValidate(value: unknown): value is ValidationPopupPatchedValidate {
    return typeof value === 'function' && (value as ValidationPopupPatchedValidate).__validationPopupOverride === true;
}

function isPatchedValidateInput(value: unknown): value is ValidationPopupPatchedValidateInput {
    return typeof value === 'function' && (value as ValidationPopupPatchedValidateInput).__validationPopupValidateInputOverride === true;
}

function isPatchedReset(value: unknown): value is ValidationPopupPatchedReset {
    return typeof value === 'function' && (value as ValidationPopupPatchedReset).__validationPopupResetOverride === true;
}

function resolveValidationScope(element: JQuery): JQuery {
    if (element.is('form')) {
        return element;
    }

    const form = element.closest('form');

    return form.length ? form : element;
}

/** Точечный отказ: `data('validation-popup', 'off')` на валидаторе либо `[data-validation-popup-scope="off"]` на предке. */
function isValidationPopupSuppressed(instance: ValidatorInstance): boolean {
    const element = instance.element;

    if (element.data(VALIDATION_POPUP_DATA_KEY) === 'off') {
        return true;
    }

    return resolveValidationScope(element).closest(VALIDATION_POPUP_SCOPE_SELECTOR).length > 0;
}

/**
 * Фабрика подменённого `validate`: при `false` показывает окно из `validator.errors()`, результат оригинала
 * и его исключение возвращает без изменений, а сбой показа валидацию не ломает (`showValidationErrorsSafely`).
 */
function createValidationPopupValidate(
    originalValidate: (...args: unknown[]) => boolean
): ValidationPopupPatchedValidate {
    const patchedValidate: ValidationPopupPatchedValidate = function (this: ValidatorInstance, ...args: unknown[]): boolean {
        if (isValidationInProgress) {
            return originalValidate.apply(this, args);
        }

        isValidationInProgress = true;

        try {
            const isValid = originalValidate.apply(this, args);

            if (!isValid && !isValidationPopupSuppressed(this)) {
                showValidationErrorsSafely(this.errors());
            }

            return isValid;
        }
        finally {
            isValidationInProgress = false;
        }
    };

    patchedValidate.__validationPopupOverride = true;

    return patchedValidate;
}

/**
 * Фабрика подменённого `validateInput` — per-field API, который Kendo вызывает по blur (`validateOnBlur`).
 * Окно SweetAlert2 отсюда НЕ открывается НИКОГДА: продуктовое решение — на blur только красная подсветка
 * поля. Для правил даты/времени/телефона blur — единственный триггер валидации, поэтому их текст не виден
 * нигде: это осознанно принятый пробел, а не забывчивость. Не добавлять сюда `showValidationErrors`, иначе
 * модальное окно будет открываться на каждый уход с поля; аргумент нормализует `resolveFieldInput`.
 */
function createValidationPopupValidateInput(
    originalValidateInput: (...args: unknown[]) => boolean
): ValidationPopupPatchedValidateInput {
    const patchedValidateInput: ValidationPopupPatchedValidateInput = function (
        this: ValidatorInstance,
        ...args: unknown[]
    ): boolean {
        const isValid = originalValidateInput.apply(this, args);
        const [input] = args;
        const field = resolveFieldInput(input);

        if (field) {
            applyFieldErrorHighlight(field, !isValid);
        }

        return isValid;
    };

    patchedValidateInput.__validationPopupValidateInputOverride = true;

    return patchedValidateInput;
}

/**
 * Фабрика подменённого `reset`: сначала оригинал (Kendo убирает свои сообщения и разметку), затем снятие нашей
 * подсветки — иначе Kendo отменил бы её лишь частично. Патч мягкий: `reset` не входит в жёсткие требования
 * установки, поэтому его отсутствие в сборке Kendo не мешает подменить `validate` и `validateInput`.
 */
function createValidationPopupReset(originalReset: (...args: unknown[]) => unknown): ValidationPopupPatchedReset {
    const patchedReset: ValidationPopupPatchedReset = function (this: ValidatorInstance, ...args: unknown[]): unknown {
        const result = originalReset.apply(this, args);

        clearFieldErrorHighlights(this.element);

        return result;
    };

    patchedReset.__validationPopupResetOverride = true;

    return patchedReset;
}

/**
 * Патчит `reset`, если он есть в прототипе и ещё не подменён: тип объявляет метод обязательным, а проверка
 * вызываемости — про НАЛИЧИЕ метода в конкретной сборке Kendo; патч мягкий, установку это не ломает.
 */
function applyValidatorResetPatch(validatorPrototype: kendo.ui.BackOfficeValidatorPrototype): void {
    const originalReset = validatorPrototype.reset;

    if (typeof originalReset !== 'function' || isPatchedReset(originalReset)) {
        return;
    }

    validatorPrototype.reset = createValidationPopupReset(originalReset);
}

/**
 * Установлен ли оверрайд: флаг модуля либо маркеры на прототипе (страховка от HMR). «Установлен» = подменены
 * ОБА жёстких метода (`validate`, `validateInput`); мягкий `reset` в признак НЕ входит: метода может не быть
 * в конкретной сборке, и его отсутствие не должно превращать рабочий оверрайд в «не установленный». Повторный
 * `installValidationPopupOverride()` доводит установку до конца, если подменён хотя бы один метод.
 */
export function isValidationPopupOverrideInstalled(): boolean {
    if (isOverrideInstalled) {
        return true;
    }

    const validatorPrototype = resolveValidatorPrototype();

    if (!validatorPrototype) {
        return false;
    }

    return isPatchedValidate(validatorPrototype.validate) && isPatchedValidateInput(validatorPrototype.validateInput);
}

/**
 * Устанавливает глобальный оверрайд: идемпотентна, исключений не бросает. `validate` (окно) и `validateInput`
 * (подсветка по blur) — жёсткие требования, `reset` патчится мягко; результаты оригиналов возвращаются
 * без изменений.
 *
 * @returns `true`, если оверрайд установлен целиком (повторный вызов тоже возвращает `true`).
 */
export function installValidationPopupOverride(): boolean {
    if (isOverrideInstalled) {
        return true;
    }

    const validatorPrototype = resolveValidatorPrototype();

    if (!validatorPrototype) {
        console.warn('[validation-popup] kendo.ui.Validator недоступен: оверрайд окна валидации не установлен.');

        return false;
    }

    if (!isPatchedValidate(validatorPrototype.validate)) {
        validatorPrototype.validate = createValidationPopupValidate(validatorPrototype.validate);
    }

    if (!isPatchedValidateInput(validatorPrototype.validateInput)) {
        validatorPrototype.validateInput = createValidationPopupValidateInput(validatorPrototype.validateInput);
    }

    applyValidatorResetPatch(validatorPrototype);

    isOverrideInstalled = true;

    return true;
}
